/**
 * Live dropdown suggestions for the hero search box of the help center (/pages/service), with two data sources:
 *
 *   1. **FAQ Q&A (local)** -- when the page is idle, fetch the HTML of /pages/faq, extract
 *      questions / answers via the data-faq-* attributes (contract defined in sections/collapsible-content.liquid)
 *      to build an index; matching on input is purely local, and clicking jumps back to /pages/faq#<anchor> and expands it.
 *      Why scrape the page rather than read Liquid: the Q&A lives in the section blocks of the page.faq template,
 *      Liquid can't read another template's section settings across pages, and Shopify search can't index them either.
 *   2. **Help articles (remote)** -- hit the Predictive Search API for blog articles; can be
 *      restricted to one blog with data-blog-handle (see filterByBlogHandle).
 *
 * This file also handles the FAQ page side: reads the URL hash and expands and highlights the matching Q&A (openFaqTarget).
 * Both sides are in one file because /pages/service and /pages/faq both include soulog-support-hero,
 * so this script tag loads on both pages.
 *
 * Completely independent of the theme's native header search (assets/predictive-search.js):
 * that one is bound to the drawer DOM + tabs + product promo slots and renders product cards via section_id,
 * while the help center only needs a plain-text list, so this is a separate implementation.
 *
 * At the end of this file the pure functions are dual-exported so tests/unit/help-search.test.mjs can require them directly.
 */
(function () {
	"use strict";

	var ENTITY_MAP = {
		"&": "&amp;",
		"<": "&lt;",
		">": "&gt;",
		'"': "&quot;",
		"'": "&#39;",
	};

	function escapeHtml(text) {
		if (typeof text !== "string") {
			return "";
		}
		return text.replace(/[&<>"']/g, function (ch) {
			return ENTITY_MAP[ch];
		});
	}

	// Escape regex metacharacters: when the user types "." / "(" they must be matched literally,
	// otherwise "." would match any character and wrap the entire title in one mark, and "(" would throw outright.
	function escapeRegExp(text) {
		return String(text).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
	}

	/**
	 * Escape first, then wrap in <mark>. The order must not be reversed -- the other way round would execute user input as HTML.
	 * Because & < > in the escaped text have become entities, the query must be escaped the same way before matching.
	 */
	function highlight(text, query) {
		var safe = escapeHtml(text);
		var term = typeof query === "string" ? query.trim() : "";
		if (!term) {
			return safe;
		}
		var safeTerm = escapeRegExp(escapeHtml(term));
		return safe.replace(new RegExp(safeTerm, "gi"), function (match) {
			return "<mark>" + match + "</mark>";
		});
	}

	function buildSuggestUrl(baseUrl, query, limit) {
		var params = new URLSearchParams();
		params.set("q", query);
		params.set("resources[type]", "article");
		params.set("resources[limit]", String(limit));
		// The only searchable article fields are title / body / author. title,body is chosen because users
		// describe symptoms ("Bluetooth keeps disconnecting") rather than title wording ("Common device pairing issues").
		params.set("resources[options][fields]", "title,body");
		return baseUrl + "?" + params.toString();
	}

	/**
	 * The Predictive Search API can't filter by blog (type=article returns articles from the whole store),
	 * so here the /blogs/<handle>/ segment of the url is used as a front-end fallback. An empty handle means no filtering.
	 */
	function filterByBlogHandle(articles, handle) {
		var list = Array.isArray(articles) ? articles : [];
		var wanted = typeof handle === "string" ? handle.trim() : "";
		if (!wanted) {
			return list;
		}
		// Include leading and trailing slashes so "faq" doesn't wrongly match "faq-archive"
		var needle = "/blogs/" + wanted + "/";
		return list.filter(function (article) {
			return typeof article.url === "string" && article.url.indexOf(needle) !== -1;
		});
	}

	function summaryText(summaryHtml, maxLen) {
		if (typeof summaryHtml !== "string" || !summaryHtml) {
			return "";
		}
		var plain = summaryHtml
			.replace(/<[^>]*>/g, " ")
			.replace(/&nbsp;/g, " ")
			.replace(/&amp;/g, "&")
			.replace(/&lt;/g, "<")
			.replace(/&gt;/g, ">")
			.replace(/&quot;/g, '"')
			.replace(/&#39;/g, "'")
			.replace(/\s+/g, " ")
			.trim();
		if (plain.length <= maxLen) {
			return plain;
		}
		return plain.slice(0, maxLen) + "…";
	}

	/* ======================= FAQ local index ======================= */

	/**
	 * Extract FAQ entries from an already-parsed document / element.
	 * For the contract see the data-faq-* comments in sections/collapsible-content.liquid;
	 * if the attributes disappear this quietly returns an empty array, so tests/unit/faq-index-markup.test.mjs pins the markup.
	 */
	function collectFaqEntries(root, pageUrl) {
		if (!root || typeof root.querySelectorAll !== "function") {
			return [];
		}
		var base = typeof pageUrl === "string" ? pageUrl : "";
		var nodes = root.querySelectorAll("[data-faq-item]");
		var entries = [];
		for (var i = 0; i < nodes.length; i += 1) {
			var item = nodes[i];
			var anchor = item.getAttribute("data-faq-anchor") || "";
			var questionEl = item.querySelector("[data-faq-question]");
			var answerEl = item.querySelector("[data-faq-answer]");
			if (!questionEl) {
				continue;
			}
			// The summary contains only a text-less svg, so textContent is the clean question text.
			var question = String(questionEl.textContent || "")
				.replace(/\s+/g, " ")
				.trim();
			if (!question) {
				continue;
			}
			var answer = answerEl
				? String(answerEl.textContent || "")
						.replace(/\s+/g, " ")
						.trim()
				: "";
			var groupEl = typeof item.closest === "function" ? item.closest("[data-faq-group]") : null;
			entries.push({
				question: question,
				answer: answer,
				anchor: anchor,
				group: groupEl ? groupEl.getAttribute("data-faq-group") || "" : "",
				groupLabel: groupEl ? groupEl.getAttribute("data-faq-group-label") || "" : "",
				url: anchor ? base + "#" + anchor : base,
			});
		}
		return entries;
	}

	/** Parse the fetched full-page HTML into FAQ entries. Returns an empty array when there is no DOMParser (Node unit tests). */
	function parseFaqIndex(html, pageUrl) {
		if (typeof html !== "string" || !html || typeof DOMParser === "undefined") {
			return [];
		}
		try {
			var doc = new DOMParser().parseFromString(html, "text/html");
			return collectFaqEntries(doc, pageUrl);
		} catch (error) {
			console.warn("[soulog-help-search] FAQ index parse failed", error);
			return [];
		}
	}

	/**
	 * Local match scoring. Question hits weigh more than answer hits, whole-string hits more than token hits;
	 * ties are stably sorted in original order (= the order ops arranged in the theme editor).
	 */
	function matchFaqEntries(entries, query, limit) {
		var list = Array.isArray(entries) ? entries : [];
		var term = typeof query === "string" ? query.trim().toLowerCase() : "";
		if (!term || !list.length) {
			return [];
		}
		var words = term.split(/\s+/).filter(Boolean);
		var scored = [];
		list.forEach(function (entry, order) {
			var question = String(entry.question || "").toLowerCase();
			var answer = String(entry.answer || "").toLowerCase();
			var score = 0;
			if (question === term) {
				score = 100;
			} else if (question.indexOf(term) === 0) {
				score = 80;
			} else if (question.indexOf(term) !== -1) {
				score = 60;
			} else if (answer.indexOf(term) !== -1) {
				score = 40;
			} else if (words.length > 1) {
				// When the whole string doesn't match, fall back to tokens: it only counts if every word appears, to avoid words like "the" pulling back every entry
				var haystack = question + " " + answer;
				var all = words.every(function (word) {
					return haystack.indexOf(word) !== -1;
				});
				if (all) {
					score = 20;
				}
			}
			if (score > 0) {
				scored.push({ entry: entry, score: score, order: order });
			}
		});
		scored.sort(function (a, b) {
			return b.score - a.score || a.order - b.order;
		});
		var max = typeof limit === "number" && limit > 0 ? limit : 5;
		return scored.slice(0, max).map(function (row) {
			return row.entry;
		});
	}

	/* ============================================================ */

	var MIN_CHARS = 2;
	var DEBOUNCE_MS = 250;
	var CACHE_LIMIT = 50;
	var SUMMARY_MAX = 90;
	var FAQ_LIMIT = 5;
	var FAQ_PREFETCH_DELAY = 300;

	function debounce(fn, wait) {
		var timer;
		return function () {
			var args = arguments;
			var self = this;
			clearTimeout(timer);
			timer = setTimeout(function () {
				fn.apply(self, args);
			}, wait);
		};
	}

	function defineElement() {
		if (typeof customElements === "undefined" || customElements.get("soulog-help-search")) {
			return;
		}

		class SoulogHelpSearch extends HTMLElement {
			connectedCallback() {
				// With data-live-disabled, don't take over at all; the form keeps its native navigation behavior
				if (this.hasAttribute("data-live-disabled")) {
					return;
				}
				// Dragging sections to reorder in the theme editor moves DOM nodes, so connectedCallback runs again.
				// The element's own listeners are still there; only the document listeners removed by disconnectedCallback need to be re-added,
				// otherwise input/keydown/focus would be bound twice (one input hits the API twice, ↓ jumps two items).
				if (this.wired) {
					document.addEventListener("click", this.onDocumentClick);
					return;
				}
				this.form = this.querySelector("form");
				this.input = this.querySelector(".soulog-support-hero__input");
				this.panel = this.querySelector(".soulog-help-search__panel");
				this.list = this.querySelector(".soulog-help-search__list");
				if (!this.form || !this.input || !this.panel || !this.list) {
					return;
				}

				this.suggestUrl = this.dataset.suggestUrl || "/search/suggest.json";
				this.limit = parseInt(this.dataset.limit, 10) || 8;
				this.blogHandle = this.dataset.blogHandle || "";
				// FAQ local index: empty data-faq-url = don't fetch, and the dropdown shows only remote articles
				this.faqUrl = this.dataset.faqUrl || "";
				this.faqLimit = parseInt(this.dataset.faqLimit, 10) || FAQ_LIMIT;
				this.faqGroupLabel = this.dataset.faqGroupLabel || "";
				this.articleGroupLabel = this.dataset.articleGroupLabel || "";
				this.faqEntries = null;
				this.noResultsText = this.dataset.noResultsText || "";
				this.noResultsLink = this.dataset.noResultsLink || "";
				this.noResultsLinkLabel = this.dataset.noResultsLinkLabel || "";
				this.cache = new Map();
				this.controller = null;
				this.items = [];
				this.activeIndex = -1;

				this.onInput = debounce(this.requestSuggestions.bind(this), DEBOUNCE_MS);
				this.input.addEventListener("input", this.onInput);
				// While JS is active, Enter never submits the form (spec §2 non-goal: no navigation to a results page)
				this.form.addEventListener("submit", function (event) {
					event.preventDefault();
				});
				this.input.addEventListener("keydown", this.onKeydown.bind(this));
				this.input.addEventListener("focus", this.onFocus.bind(this));
				this.addEventListener("focusout", this.onFocusOut.bind(this));
				this.onDocumentClick = this.handleDocumentClick.bind(this);
				document.addEventListener("click", this.onDocumentClick);
				// Safari/Firefox/iOS don't give links focus by default, so the focusout triggered by mousedown
				// closes the panel first and click never gets dispatched. Just prevent focus from leaving the input.
				this.panel.addEventListener("mousedown", function (event) {
					event.preventDefault();
				});

				this.wired = true;
				this.prefetchFaqIndex();
			}

			/**
			 * Fetch the FAQ page once when idle to build the index. Deliberately not stored in sessionStorage -- after ops edit the Q&A
			 * in the theme editor, users should see the new version in the same session; a single fetch is only a few dozen KB, not worth
			 * the risk of "seeing stale Q&A".
			 */
			prefetchFaqIndex() {
				if (!this.faqUrl || this.faqEntries) {
					return;
				}
				var self = this;
				var run = function () {
					fetch(self.faqUrl, { headers: { Accept: "text/html" } })
						.then(function (response) {
							return response.ok ? response.text() : "";
						})
						.then(function (html) {
							self.faqEntries = parseFaqIndex(html, self.faqUrl);
						})
						.catch(function (error) {
							// If it can't be fetched, degrade to "remote articles only"; search remains usable
							console.warn("[soulog-help-search] FAQ index fetch failed", error);
							self.faqEntries = [];
						});
				};
				if (typeof requestIdleCallback === "function") {
					requestIdleCallback(run, { timeout: 2000 });
				} else {
					setTimeout(run, FAQ_PREFETCH_DELAY);
				}
			}

			disconnectedCallback() {
				if (this.onDocumentClick) {
					document.removeEventListener("click", this.onDocumentClick);
				}
				if (this.controller) {
					this.controller.abort();
				}
			}

			requestSuggestions() {
				var query = this.input.value.trim();
				// Whichever branch is taken (cache hit / too few characters / actual request), first claim the in-flight request,
				// otherwise the aborted old request's .finally would remove is-loading only after the new request,
				// or the old request's callback would keep changing state when it wasn't aborted.
				if (this.controller) {
					this.controller.abort();
					this.controller = null;
					// The claim guard makes the aborted request's .finally return immediately, so is-loading
					// must be removed here -- otherwise when a later cache-hit or too-few-characters early-return branch is taken nobody removes it,
					// and the panel stays stuck in the opacity:.6 loading state (reproducible by backspacing to the previous word).
					this.panel.classList.remove("is-loading");
				}
				if (query.length < MIN_CHARS) {
					this.close();
					return;
				}
				// Compute local FAQ first -- zero latency; draw this batch while the remote request is still in flight
				var faqRows = matchFaqEntries(this.faqEntries || [], query, this.faqLimit);
				if (!this.suggestUrl) {
					this.render(faqRows, [], query);
					return;
				}
				if (this.cache.has(query)) {
					this.render(faqRows, this.cache.get(query), query);
					return;
				}
				if (faqRows.length) {
					this.render(faqRows, [], query);
				}
				var controller = new AbortController();
				this.controller = controller;
				this.panel.classList.add("is-loading");

				var self = this;
				fetch(buildSuggestUrl(this.suggestUrl, query, this.limit), {
					signal: controller.signal,
				})
					.then(function (response) {
						// Stale requests' callbacks don't touch any state, so they don't hijack the handling of the new request's response.
						if (self.controller !== controller) {
							return;
						}
						if (!response.ok) {
							throw new Error("Predictive search failed: " + response.status);
						}
						return response.json();
					})
					.then(function (data) {
						if (self.controller !== controller) {
							return;
						}
						var articles = (data && data.resources && data.resources.results && data.resources.results.articles) || [];
						var rows = filterByBlogHandle(articles, self.blogHandle);
						if (self.cache.size >= CACHE_LIMIT) {
							self.cache.delete(self.cache.keys().next().value);
						}
						self.cache.set(query, rows);
						// By the time the response comes back the user may have clicked away; the panel shouldn't pop back up then.
						// The synchronous cache-hit branch naturally has focus, so it doesn't need this gate.
						var stillFocused = self.contains(self.getRootNode().activeElement);
						if (self.input.value.trim() === query && stillFocused) {
							self.render(matchFaqEntries(self.faqEntries || [], query, self.faqLimit), rows, query);
						}
					})
					.catch(function (error) {
						if (self.controller !== controller) {
							return;
						}
						if (error.name === "AbortError") {
							return;
						}
						console.warn("[soulog-help-search]", error);
						self.close();
					})
					.finally(function () {
						if (self.controller !== controller) {
							return;
						}
						self.panel.classList.remove("is-loading");
					});
			}

			/**
			 * Both result groups share one <ul>: FAQ first (local, more accurate hits), help articles after.
			 * Group headings are li elements with role="presentation" and are not in this.items, so keyboard ↑↓
			 * naturally moves continuously across groups without stopping on headings.
			 */
			render(faqRows, articleRows, query) {
				// Record which query this batch of results belongs to; isOpen relies on it to prevent "results out of sync with the input".
				this.renderedQuery = query;
				this.activeIndex = -1;
				// Clear the highlight pointer left over from the previous round of results, otherwise the id points to a removed node
				this.input.removeAttribute("aria-activedescendant");
				var faq = Array.isArray(faqRows) ? faqRows : [];
				var articles = Array.isArray(articleRows) ? articleRows : [];
				if (!faq.length && !articles.length) {
					this.renderEmpty(query);
					return;
				}
				var self = this;
				var index = 0;
				var html = "";

				function optionHtml(url, title, summary, chip) {
					var out =
						'<li class="soulog-help-search__item" role="option" aria-selected="false" id="HelpSearchOption' +
						index +
						'">' +
						'<a class="soulog-help-search__link" href="' +
						escapeHtml(url) +
						'" tabindex="-1">' +
						'<span class="soulog-help-search__title">' +
						highlight(title, query) +
						(chip ? '<span class="soulog-help-search__chip">' + escapeHtml(chip) + "</span>" : "") +
						"</span>" +
						(summary ? '<span class="soulog-help-search__summary">' + highlight(summary, query) + "</span>" : "") +
						"</a></li>";
					index += 1;
					return out;
				}

				function groupHtml(label) {
					return label
						? '<li class="soulog-help-search__group" role="presentation">' + escapeHtml(label) + "</li>"
						: "";
				}

				if (faq.length) {
					html += groupHtml(self.faqGroupLabel);
					faq.forEach(function (entry) {
						var summary = summaryText(entry.answer, SUMMARY_MAX);
						html += optionHtml(entry.url, entry.question, summary, entry.groupLabel);
					});
				}
				if (articles.length) {
					html += groupHtml(self.articleGroupLabel);
					articles.forEach(function (article) {
						html += optionHtml(article.url, article.title, summaryText(article.summary_html, SUMMARY_MAX), "");
					});
				}
				this.list.innerHTML = html;
				this.items = Array.prototype.slice.call(this.list.querySelectorAll(".soulog-help-search__item"));
				this.items.forEach(function (item, index) {
					item.addEventListener("mouseenter", function () {
						self.setActive(index);
					});
				});
				this.open();
			}

			renderEmpty(query) {
				// Record which query this batch of results belongs to; isOpen relies on it to prevent "results out of sync with the input".
				this.renderedQuery = query;
				// Whichever branch is taken, the previous results must be cleared first, otherwise onFocus() would
				// reopen the panel and show the old query's results as the current query's results.
				this.items = [];
				this.list.innerHTML = "";
				// If the copy isn't configured, don't pop an empty panel, to avoid a blank box
				if (!this.noResultsText) {
					this.close();
					return;
				}
				// query here is the replacement string; pass it as a function so that "$&" / "$`" etc. are not
				// interpreted as special tokens by String.replace, and the user's input text is inserted verbatim.
				var message = escapeHtml(
					this.noResultsText.replace("[query]", function () {
						return query;
					}),
				);
				var linkHtml = "";
				if (this.noResultsLink && this.noResultsLinkLabel) {
					linkHtml =
						'<a class="soulog-help-search__empty-link" href="' +
						escapeHtml(this.noResultsLink) +
						'">' +
						escapeHtml(this.noResultsLinkLabel) +
						"</a>";
				}
				this.list.innerHTML = '<li class="soulog-help-search__empty">' + message + linkHtml + "</li>";
				this.open();
			}

			setActive(index) {
				this.items.forEach(function (item) {
					item.classList.remove("is-active");
					item.setAttribute("aria-selected", "false");
				});
				this.activeIndex = index;
				if (index < 0 || !this.items[index]) {
					this.input.removeAttribute("aria-activedescendant");
					return;
				}
				var item = this.items[index];
				item.classList.add("is-active");
				item.setAttribute("aria-selected", "true");
				this.input.setAttribute("aria-activedescendant", item.id);
				// Scroll the item into view when the keyboard moves outside the viewport
				if (typeof item.scrollIntoView === "function") {
					item.scrollIntoView({ block: "nearest" });
				}
			}

			open() {
				this.panel.hidden = false;
				this.input.setAttribute("aria-expanded", "true");
			}

			close() {
				this.panel.hidden = true;
				this.input.setAttribute("aria-expanded", "false");
				this.input.removeAttribute("aria-activedescendant");
				this.activeIndex = -1;
			}

			onKeydown(event) {
				// Enter during IME composition only confirms a candidate word, it isn't "select a result"
				if (event.isComposing) {
					return;
				}
				// The results in the panel must come from the current input, otherwise during debounce/network round trips,
				// changing the word and then pressing Enter or arrow keys would jump/move over the previous word's stale results.
				var isOpen = !this.panel.hidden && this.items.length > 0 && this.renderedQuery === this.input.value.trim();

				if (event.key === "Escape") {
					this.close();
					return;
				}

				if (event.key === "ArrowDown" || event.key === "ArrowUp") {
					if (!isOpen) {
						return;
					}
					event.preventDefault(); // prevent the caret from jumping to the start/end of the input
					var step = event.key === "ArrowDown" ? 1 : -1;
					var count = this.items.length;
					// When activeIndex is -1 (no highlight), ↓ goes to 0 and ↑ goes to the end
					var next = (this.activeIndex + step + count + (this.activeIndex < 0 && step < 0 ? 1 : 0)) % count;
					this.setActive(next);
					return;
				}

				if (event.key === "Enter") {
					// Never submit the form, whether or not there are results
					event.preventDefault();
					if (!isOpen) {
						return;
					}
					var target = this.activeIndex >= 0 ? this.items[this.activeIndex] : this.items[0];
					var link = target && target.querySelector(".soulog-help-search__link");
					if (link) {
						window.location.href = link.getAttribute("href");
					}
				}
			}

			onFocus() {
				// On refocus with an existing query, reuse the last results directly without re-requesting;
				// but the results must come from the current query, otherwise the old query's results would pop back up.
				if (
					this.items.length > 0 &&
					this.input.value.trim().length >= MIN_CHARS &&
					this.renderedQuery === this.input.value.trim()
				) {
					this.open();
				}
			}

			onFocusOut(event) {
				// Clicking a result item triggers focusout first; use relatedTarget to check whether focus is still inside the component,
				// otherwise the panel would close before the click takes effect, making items unclickable.
				if (event.relatedTarget && this.contains(event.relatedTarget)) {
					return;
				}
				this.close();
			}

			handleDocumentClick(event) {
				if (!this.contains(event.target)) {
					this.close();
				}
			}
		}

		customElements.define("soulog-help-search", SoulogHelpSearch);
	}

	defineElement();

	/* ================= FAQ page side: expand on hash landing ================= */

	/**
	 * When landing from /pages/faq#Details-xxx, expand the matching Q&A and scroll it to the middle of the view (no highlight; removed per requirement on 2026-09-26).
	 * We must scroll ourselves: the browser's native hash jump happens while the details is still collapsed, and after expanding the layout
	 * changes, so the native landing position is off.
	 */
	function openFaqTarget(rawId) {
		var id = typeof rawId === "string" ? rawId.replace(/^#/, "") : "";
		if (!id || typeof document === "undefined") {
			return false;
		}
		var target;
		try {
			target = document.getElementById(decodeURIComponent(id));
		} catch (error) {
			target = document.getElementById(id);
		}
		if (!target) {
			return false;
		}
		var details = target.tagName === "DETAILS" ? target : target.querySelector("details");
		if (details && !details.open) {
			details.open = true;
		}
		var scrollTarget = details || target;
		// ⚠️ Deliberately not waiting a frame with requestAnimationFrame here: in background tabs (cmd+click
		// to open a link in a new tab) rAF never fires, and the scroll would be lost.
		// details.open = true is synchronous, and scrollIntoView flushes layout itself,
		// so measuring the position directly is accurate; no need to wait a frame.
		if (typeof scrollTarget.scrollIntoView === "function") {
			scrollTarget.scrollIntoView({ block: "center", behavior: "smooth" });
		}
		return true;
	}

	function handleFaqHash() {
		if (typeof window === "undefined" || !window.location || !window.location.hash) {
			return;
		}
		openFaqTarget(window.location.hash);
	}

	if (typeof window !== "undefined" && typeof document !== "undefined") {
		// The script is defer, so the DOM is ready by the time it gets here; handle the initial landing directly
		handleFaqHash();
		// When already on the FAQ page and clicking another item from the dropdown, only the hash changes; no reload
		window.addEventListener("hashchange", handleFaqHash);
	}

	var utils = {
		buildSuggestUrl,
		escapeHtml,
		highlight,
		filterByBlogHandle,
		summaryText,
		collectFaqEntries,
		parseFaqIndex,
		matchFaqEntries,
	};

	if (typeof module === "object" && module && module.exports) {
		module.exports = utils;
	}
	if (typeof globalThis !== "undefined") {
		globalThis.SoulogHelpSearchUtils = utils;
	}
})();
